import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Trash2, Search, Home } from 'lucide-react';
import { Header, Footer } from '@/components/layout';
import FloatingChat from '@/components/layout/FloatingChat';
import { useFavoritesContext } from '@/components/providers/FavoritesProvider';
import { useSiteConfigContext } from '@/components/providers/SiteConfigProvider';
import PropertyCard from '@/components/PropertyCard';
import { cn } from '@/lib/utils';

export default function Favorites() {
  const navigate = useNavigate();
  const { favorites, clearFavorites, removeFavorite } = useFavoritesContext();
  const { configuration } = useSiteConfigContext();
  const [showConfirmClear, setShowConfirmClear] = React.useState(false);

  const handleClearAll = () => {
    if (showConfirmClear) {
      clearFavorites();
      setShowConfirmClear(false);
    } else {
      setShowConfirmClear(true);
      setTimeout(() => setShowConfirmClear(false), 3000);
    }
  };

  const handleContactClick = () => {
    const whatsapp = '5519982589820';
    const message = encodeURIComponent('Olá! Gostaria de mais informações sobre os imóveis.');
    window.open(`https://wa.me/${whatsapp}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full max-w-[1920px] mx-auto">
        <Header 
          onNavigateToProperties={() => navigate('/search-grid')}
          onNavigateToAbout={() => navigate('/#sobre')}
          onAnnounceClick={() => navigate('/anuncie')}
        />

        {/* Hero Section */}
        <section className="bg-gradient-to-b from-gray-50 to-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 lg:px-20 py-12">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
                  <Heart className="w-8 h-8 text-red-500 fill-red-500" />
                </div>
                <div>
                  <h1 className="text-3xl lg:text-4xl font-black text-neutral-black">
                    Meus Favoritos
                  </h1>
                  <p className="text-lg text-neutral-gray-light mt-1">
                    {favorites.length === 0 
                      ? 'Nenhum imóvel salvo ainda'
                      : `${favorites.length} ${favorites.length === 1 ? 'imóvel salvo' : 'imóveis salvos'}`
                    }
                  </p>
                </div>
              </div>

              {favorites.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className={cn(
                    'flex items-center gap-2 px-6 py-3 rounded-full',
                    'font-medium transition-all duration-200',
                    showConfirmClear
                      ? 'bg-red-500 text-white hover:bg-red-600'
                      : 'bg-white border-2 border-red-500 text-red-600 hover:bg-red-50'
                  )}
                >
                  <Trash2 className="w-5 h-5" />
                  {showConfirmClear ? 'Confirmar exclusão?' : 'Limpar tudo'}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-7xl mx-auto px-4 lg:px-20 py-12">
          {favorites.length === 0 ? (
            // Empty State
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                <Heart className="w-12 h-12 text-gray-400" />
              </div>
              
              <h2 className="text-2xl font-bold text-neutral-black mb-2">
                Nenhum favorito ainda
              </h2>
              
              <p className="text-lg text-neutral-gray-light max-w-md mb-8">
                Comece a salvar seus imóveis favoritos clicando no ícone de coração
              </p>

              <div className="flex gap-4">
                <button
                  onClick={() => navigate('/search-grid')}
                  className="flex items-center gap-2 px-6 py-3 rounded-full bg-neutral-black text-white hover:bg-neutral-black/90 transition-colors"
                >
                  <Search className="w-5 h-5" />
                  Buscar Imóveis
                </button>
                
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center gap-2 px-6 py-3 rounded-full border-2 border-neutral-black text-neutral-black hover:bg-gray-50 transition-colors"
                >
                  <Home className="w-5 h-5" />
                  Página Inicial
                </button>
              </div>
            </div>
          ) : (
            // Grid de Favoritos
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-neutral-black">
                  Seus Imóveis Salvos
                </h2>
                
                <p className="text-sm text-neutral-gray-light">
                  Últimos salvos aparecem primeiro
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favorites
                  .sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime())
                  .map((fav) => (
                    <div key={fav.id} className="relative group">
                      <PropertyCard
                        id={fav.id}
                        title={fav.title}
                        code={fav.code}
                        image={fav.image || '/img-default.jpg'}
                        price={fav.price || 'Sob consulta'}
                        address={fav.address || ''}
                        attributes=""
                        saleType="Locação"
                        onClick={() => {
                          if (fav.code) {
                            navigate(`/imovel/${fav.code}`);
                          } else {
                            navigate(`/property/${fav.id}`);
                          }
                        }}
                      />
                      
                      {/* Quick Remove Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFavorite(fav.id);
                        }}
                        className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        title="Remover dos favoritos"
                      >
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-50 transition-colors">
                          <Trash2 className="w-5 h-5 text-red-500" />
                        </div>
                      </button>
                    </div>
                  ))}
              </div>

              {/* Actions */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex flex-col items-center gap-4">
                  <p className="text-neutral-gray-light text-center max-w-2xl">
                    Encontrou o imóvel ideal? Entre em contato conosco para agendar uma visita
                  </p>
                  
                  <button
                    onClick={handleContactClick}
                    className="px-8 py-4 rounded-full bg-green-500 text-white font-semibold hover:bg-green-600 transition-colors"
                  >
                    Falar com um Corretor
                  </button>
                </div>
              </div>
            </>
          )}
        </section>

        <Footer onContactClick={handleContactClick} />
      </div>

      <FloatingChat
        position="bottom-right"
        size="lg"
        onClick={handleContactClick}
      />
    </div>
  );
}
